import {
  useAssignUserRole,
  useRemoveUserRole,
  useRoles,
  useUserRoles,
} from '@/domains/access-control';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import type { RoleSummary } from '@skewb-works/contracts';
import { Lock, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

const ROLE_OPTION_CLASS =
  'flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-accent/50 cursor-pointer';

/**
 * Manage a user's DB access-control role. A user holds a single role, so the options are a
 * radio group (plus "No role") sourced from `useRoles`, and the current assignment comes from
 * `useUserRoles`. The choice is a local draft, persisted only when the user clicks Save
 * (assign the new role, remove the old); Cancel and closing discard it.
 */
export function AssignRoleDialog({ open, onOpenChange, userId }: AssignRoleDialogProps) {
  const numericUserId = Number(userId);

  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: userRolesData, isLoading: userRolesLoading } = useUserRoles(numericUserId);
  const assign = useAssignUserRole();
  const remove = useRemoveUserRole();

  const roles: RoleSummary[] = rolesData?.data ?? [];
  const assignedRoleIds = useMemo(
    () => new Set<number>(userRolesData?.roleIds ?? []),
    [userRolesData],
  );

  // Surface the user's currently-assigned roles at the top of the list, just under the built-in
  // Admin role (the system role with the lock badge — note other "…Administrator" roles also have
  // isAdmin but aren't system roles), so it's obvious what they already have. Ordering keys off the
  // persisted assignments — not the live draft — so rows don't jump around as the selection
  // changes. Within each rank, roles are grouped by provider (Cadent before LDP, everything else
  // last) so the list reads as coherent sections instead of a mixed jumble.
  const orderedRoles = useMemo<RoleSummary[]>(() => {
    const rank = (role: RoleSummary) => {
      if (role.isAdmin && role.isSystem) return 0;
      if (assignedRoleIds.has(role.id)) return 1;
      return 2;
    };
    // Group by the provider prefix in the role name so Cadent roles cluster ahead of LDP ones.
    const groupRank = (role: RoleSummary) => {
      if (/^cadent\b/i.test(role.name)) return 0;
      if (/^ldp\b/i.test(role.name)) return 1;
      return 2;
    };
    return roles
      .map((role, index) => ({ role, index }))
      .sort(
        (a, b) =>
          rank(a.role) - rank(b.role) || groupRank(a.role) - groupRank(b.role) || a.index - b.index,
      )
      .map(({ role }) => role);
  }, [roles, assignedRoleIds]);

  // `undefined` = nothing picked this session (fall back to the persisted role); `null` = "No role".
  // Reset on the false→true open transition only, so a background useUserRoles refetch while
  // open can't clobber an in-progress pick; Cancel/close still discard because the next open resets.
  const [draft, setDraft] = useState<number | null | undefined>(undefined);
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) setDraft(undefined);
    prevOpen.current = open;
  }, [open]);

  // The hooks return `data: undefined` while loading, on error and on non-200 responses, so both
  // lists must be present before the draft means anything: otherwise "No role" would look selected
  // for a user who really holds roles, and Save would leave or strip them without any signal.
  const isReady = rolesData !== undefined && userRolesData !== undefined && roles.length > 0;

  const persistedRoleId = orderedRoles.find((role) => assignedRoleIds.has(role.id))?.id ?? null;
  const selected = draft === undefined ? persistedRoleId : draft;

  const handleSave = async () => {
    if (!isReady) return;
    const roleIdToAdd = selected !== null && !assignedRoleIds.has(selected) ? selected : null;
    const roleIdsToRemove = [...assignedRoleIds].filter((id) => id !== selected);
    try {
      // Assign before removing so a failed assign never leaves the user without their current role.
      if (roleIdToAdd !== null) {
        await assign.mutateAsync({ userId: numericUserId, roleId: roleIdToAdd });
      }
      await Promise.all(
        roleIdsToRemove.map((roleId) => remove.mutateAsync({ userId: numericUserId, roleId })),
      );
      onOpenChange(false);
    } catch {
      // Mutation failures are already toasted by the global MutationCache error handler.
    }
  };

  const isSaving = assign.isPending || remove.isPending;
  const radioGroupName = `assign-role-${numericUserId}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage Roles</DialogTitle>
          <DialogDescription>
            Choose the access-control role for this user. A user can hold only one role. Roles grant
            permissions across the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" />
            <Label className="text-sm">Role</Label>
          </div>

          <div
            role="radiogroup"
            aria-label="Role"
            className="space-y-1 rounded-md border border-border p-1"
          >
            {(rolesLoading || userRolesLoading) && (
              <span className="text-xs text-muted-foreground">Loading…</span>
            )}
            {!rolesLoading && roles.length === 0 && (
              <span className="text-xs text-muted-foreground italic">No roles available.</span>
            )}
            {roles.length > 0 && (
              <label className={ROLE_OPTION_CLASS}>
                <input
                  type="radio"
                  name={radioGroupName}
                  aria-label="No role"
                  checked={isReady && selected === null}
                  disabled={!isReady}
                  onChange={() => setDraft(null)}
                  className="accent-[#E84A2B]"
                />
                <span className="text-sm text-foreground">No role</span>
              </label>
            )}
            {orderedRoles.map((role) => (
              <label key={role.id} className={ROLE_OPTION_CLASS}>
                <input
                  type="radio"
                  name={radioGroupName}
                  aria-label={role.name}
                  checked={isReady && selected === role.id}
                  disabled={!isReady}
                  onChange={() => setDraft(role.id)}
                  className="accent-[#E84A2B]"
                />
                <div className="flex flex-col">
                  <span className="flex items-center gap-2 text-sm text-foreground">
                    {role.name}
                    {role.isSystem && (
                      <span
                        className="flex items-center gap-1 text-xs text-muted-foreground"
                        title="System role"
                      >
                        <Lock className="size-3" />
                        System
                      </span>
                    )}
                  </span>
                  {role.description && (
                    <span className="text-xs text-muted-foreground">{role.description}</span>
                  )}
                </div>
              </label>
            ))}
          </div>
          {assignedRoleIds.size > 1 && (
            <p role="status" className="text-xs text-muted-foreground">
              {`This user currently has ${assignedRoleIds.size} roles. Saving keeps only the selected role.`}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            className="text-white"
            style={{ backgroundColor: '#E84A2B' }}
            onClick={handleSave}
            disabled={isSaving || !isReady}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
